/* The live wire.
 *
 * The server has broadcast every change to every signed-in device since the
 * beginning — `realtime.broadcast(userId, …)` is called from the plan routes,
 * the task routes, the appointment routes and the reminder scheduler. This
 * client listened to none of it, so a day planned on a phone did not appear on
 * the laptop until something happened to refetch, and a reminder that fired
 * while the tab was open arrived nowhere at all.
 *
 * What this is not: a state channel. Nothing here trusts the socket as a source
 * of truth. A message says *what changed*, and the answer is always to
 * invalidate the query that owns it and let React Query refetch through the
 * same API every other path uses. Two ways to write the same state into a cache
 * is how a client starts disagreeing with its own server, and a socket is the
 * least reliable of the two.
 *
 * Reconnection is deliberately unclever: back off, cap at half a minute, and
 * refetch everything on reconnect because an unknown amount was missed while
 * the socket was down.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getToken } from '../api';
import { showThroughWorker } from './push';
import { followAccountTheme } from './theme';

/** Everything the server sends, as far as this client cares. */
interface Message {
  type: string;
  plan?: { date: string };
  [key: string]: unknown;
}

const RETRY_MIN = 1_000, RETRY_MAX = 30_000;

export function useRealtime(signedIn: boolean) {
  const qc = useQueryClient();
  const ws = useRef<WebSocket | null>(null);
  const retry = useRef(RETRY_MIN);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closing = useRef(false);

  useEffect(() => {
    if (!signedIn) return;
    closing.current = false;

    const connect = () => {
      const token = getToken();
      if (!token || closing.current) return;

      const url = (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws';
      let socket: WebSocket;
      try { socket = new WebSocket(url); } catch { return schedule(); }
      ws.current = socket;

      socket.onopen = () => {
        socket.send(JSON.stringify({ type: 'auth', token }));
      };

      socket.onmessage = ev => {
        let msg: Message;
        try { msg = JSON.parse(ev.data as string); } catch { return; }

        switch (msg.type) {
          case 'auth':
            /* Connected and identified. Only now is the backoff reset — an
               endpoint that accepts a socket and rejects the token would
               otherwise be a reconnect loop at full speed. */
            if (msg.ok) {
              retry.current = RETRY_MIN;
              /* An unknown amount was missed while this was down. */
              void qc.invalidateQueries();
            }
            break;

          case 'plan':
            /* By date, so a message about tomorrow does not refetch today. */
            void qc.invalidateQueries({
              queryKey: msg.plan?.date ? ['plan', msg.plan.date] : ['plan']
            });
            void qc.invalidateQueries({ queryKey: ['month'] });
            void qc.invalidateQueries({ queryKey: ['week'] });
            void qc.invalidateQueries({ queryKey: ['year'] });
            break;

          case 'task':
          case 'tasks.changed':
          case 'task.deleted':
            void qc.invalidateQueries({ queryKey: ['tasks'] });
            break;

          case 'appointment':
          case 'appointments.changed':
          case 'appointment.deleted':
            void qc.invalidateQueries({ queryKey: ['appointments'] });
            void qc.invalidateQueries({ queryKey: ['plan'] });
            void qc.invalidateQueries({ queryKey: ['month'] });
            break;

          case 'occasions':
            /* Birthdays live on the year, are drawn on the week and the month,
               and are added from a dialog that may be open on another device. */
            void qc.invalidateQueries({ queryKey: ['occasions'] });
            void qc.invalidateQueries({ queryKey: ['year'] });
            void qc.invalidateQueries({ queryKey: ['week'] });
            void qc.invalidateQueries({ queryKey: ['month'] });
            break;

          case 'intentions':
          case 'activity-types':
            void qc.invalidateQueries({ queryKey: ['balance'] });
            void qc.invalidateQueries({ queryKey: ['hobbies'] });
            void qc.invalidateQueries({ queryKey: ['rewards'] });
            void qc.invalidateQueries({ queryKey: ['activity-types'] });
            break;

          case 'reminder':
            /* Through the service worker, with the reminder's own id as the
               tag — so the socket copy and the push copy of the same reminder
               replace one another instead of arriving twice. If the worker is
               not available, the tab is open anyway and the day will show it. */
            showThroughWorker(msg);
            /* A reminder means a block just became due; the day may have been
               re-planned around it. */
            void qc.invalidateQueries({ queryKey: ['plan'] });
            break;

          case 'timer':
            void qc.invalidateQueries({ queryKey: ['timer'] });
            break;

          /* The ones only the prototype listened for. Retiring it would have
             left each of these shouting into the void: a connection accepted
             on a phone, a ritual added on a laptop, a reminder snoozed from a
             notification — none of it reaching the other open screen. */
          case 'people':
          case 'message':
            void qc.invalidateQueries({ queryKey: ['social'] });
            void qc.invalidateQueries({ queryKey: ['social-preview'] });
            void qc.invalidateQueries({ queryKey: ['friends'] });
            void qc.invalidateQueries({ queryKey: ['meetings'] });
            void qc.invalidateQueries({ queryKey: ['people'] });
            break;

          case 'rituals':
            void qc.invalidateQueries({ queryKey: ['rituals'] });
            void qc.invalidateQueries({ queryKey: ['plan'] });
            void qc.invalidateQueries({ queryKey: ['week'] });
            void qc.invalidateQueries({ queryKey: ['month'] });
            break;

          case 'sleep':
            void qc.invalidateQueries({ queryKey: ['sleep'] });
            void qc.invalidateQueries({ queryKey: ['plan'] });
            break;

          case 'reminder.acted':
            /* Started, pushed or skipped from the notification itself. */
            void qc.invalidateQueries({ queryKey: ['plan'] });
            void qc.invalidateQueries({ queryKey: ['timer'] });
            void qc.invalidateQueries({ queryKey: ['tasks'] });
            break;

          case 'user':
            /* The account changed on another device. Its theme travels with
               it, so a laptop changing it repaints a signed-in phone. */
            followAccountTheme((msg.user as { theme?: string | null } | undefined)?.theme);
            void qc.invalidateQueries();
            break;

          case 'preferences':
            /* Working hours, sleep, time zone or the plan's own settings —
               any of which changes what every screen draws. */
            void qc.invalidateQueries();
            break;

          case 'tasks-changed':
            /* The old spelling. Nothing sends it any more; kept so a page
               loaded before a deploy still updates while the deploy rolls out. */
            void qc.invalidateQueries({ queryKey: ['tasks'] });
            break;

          case 'presence':
            /* Which devices are online. Nothing on screen depends on it yet,
               and it changes nothing that needs fetching. */
            break;
        }
      };

      socket.onclose = () => { ws.current = null; schedule(); };
      /* `onerror` is always followed by `onclose`, so reconnecting here as well
         would double the attempts. */
      socket.onerror = () => {};
    };

    const schedule = () => {
      if (closing.current) return;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(connect, retry.current);
      retry.current = Math.min(RETRY_MAX, retry.current * 2);
    };

    connect();

    return () => {
      closing.current = true;
      if (timer.current) clearTimeout(timer.current);
      try { ws.current?.close(); } catch { /* already gone */ }
      ws.current = null;
    };
  }, [signedIn, qc]);
}
